
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import UserManagement from './components/UserManagement';
import Reports from './components/Reports';
import { createClient } from '@supabase/supabase-js';

// Supabase client'ı oluştur
const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

export default function AdminPage() {
  const [activeTab, setActiveTab] = useState('users');
  const [cateringEmail, setCateringEmail] = useState('');
  const [cateringEmailLoading, setCateringEmailLoading] = useState(false);
  const [cateringEmailStatus, setCateringEmailStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [loading, setLoading] = useState(true);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const navigate = useNavigate();

  // Auth token'ı localStorage'dan al veya basit token oluştur
  const getAuthToken = async () => {
    try {
      // Önce localStorage'dan token'ı kontrol et
      let token = localStorage.getItem('auth_token');
      
      if (!token) {
        // Token yoksa Supabase session'ını kontrol et
        const { data: { session } } = await supabase.auth.getSession();
        
        if (session?.access_token) {
          token = session.access_token;
          localStorage.setItem('auth_token', token);
        } else {
          // Session da yoksa basit bir token oluştur
          token = 'temp_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
          localStorage.setItem('auth_token', token);
        }
      }
      
      return token;
    } catch (error) {
      console.error('Token alma hatası:', error);
      // Hata durumunda geçici token oluştur
      const tempToken = 'temp_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
      localStorage.setItem('auth_token', tempToken);
      return tempToken;
    }
  };

  useEffect(() => {
    // Basit yetki kontrolü - sadece token varlığını kontrol et
    const checkAccess = () => {
      console.log('🔐 Admin sayfası yükleniyor...');
      
      const token = localStorage.getItem('auth_token');
      const userRole = localStorage.getItem('user_role');
      const userName = localStorage.getItem('user_name');
      
      console.log('Debug Bilgileri:');
      console.log('- Token:', token ? 'Mevcut' : 'Yok');
      console.log('- User Role:', userRole);
      console.log('- User Name:', userName);
      console.log('- Environment:', {
        supabaseUrl: import.meta.env.VITE_PUBLIC_SUPABASE_URL ? 'Mevcut' : 'Yok',
        mode: import.meta.env.MODE,
        dev: import.meta.env.DEV
      });
      
      // Token yoksa login sayfasına yönlendir
      if (!token) {
        console.log('❌ Token bulunamadı, login sayfasına yönlendiriliyor');
        navigate('/login');
        return;
      }
      
      console.log('✅ Admin sayfası yüklendi');
      setLoading(false);
    };

    checkAccess();
  }, [navigate]);

  // İkram e-posta ayarlarını yükle
  useEffect(() => {
    const loadCateringSettings = async () => {
      try {
        const token = await getAuthToken();
        const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/get-catering-settings`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          const data = await response.json();
          setCateringEmail(data.email || '');
        }
      } catch (error) {
        console.error('İkram ayarları yüklenemedi:', error);
      }
    };

    if (activeTab === 'settings') {
      loadCateringSettings();
    }
  }, [activeTab]);

  // İkram e-posta ayarlarını kaydet
  const saveCateringSettings = async () => {
    if (!cateringEmail.trim()) {
      setCateringEmailStatus('error');
      return;
    }

    try {
      setCateringEmailLoading(true);
      setCateringEmailStatus('idle');

      const token = await getAuthToken();
      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/save-catering-settings`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: cateringEmail.trim()
        }),
      });

      if (response.ok) {
        setCateringEmailStatus('success');
        setTimeout(() => setCateringEmailStatus('idle'), 3000);
      } else {
        setCateringEmailStatus('error');
      }
    } catch (error) {
      console.error('İkram ayarları kaydedilemedi:', error);
      setCateringEmailStatus('error');
    } finally {
      setCateringEmailLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user_name');
    localStorage.removeItem('user_email');
    localStorage.removeItem('user_role');
    localStorage.removeItem('user_id');
    navigate('/login');
  };

  const handleBackToHome = () => {
    navigate('/');
    setShowUserMenu(false);
  };

  // Kullanıcı bilgilerini al
  const userName = localStorage.getItem('user_name') || 'Admin';
  const userRole = localStorage.getItem('user_role') || 'admin';

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="pt-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="flex items-center justify-center py-12">
              <i className="ri-loader-4-line animate-spin text-2xl text-blue-600 mr-3"></i>
              <span className="text-gray-600">Sayfa yükleniyor...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <h1 className="text-xl font-bold text-gray-900" style={{ fontFamily: '"Pacifico", serif' }}>
                  Admin Panel
                </h1>
              </div>
            </div>

            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-3">
                <span className="text-sm text-gray-600">Hoş geldiniz, {userName}</span>
                <div className="relative">
                  <div 
                    className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center cursor-pointer"
                    onClick={() => setShowUserMenu(!showUserMenu)}
                  >
                    <i className="ri-user-line text-gray-600"></i>
                  </div>
                  
                  {showUserMenu && (
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50 border border-gray-200">
                      <div className="px-4 py-2 text-sm text-gray-700 border-b border-gray-100">
                        <div className="font-medium">{userName}</div>
                        <div className="text-xs text-gray-500 capitalize">
                          {userRole === 'admin' ? 'Admin' : 'Kullanıcı'}
                        </div>
                      </div>
                      
                      <button
                        onClick={handleBackToHome}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer"
                      >
                        <i className="ri-home-line mr-2"></i>
                        Ana Sayfaya Dön
                      </button>
                      
                      <button
                        onClick={() => setShowUserMenu(false)}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer"
                      >
                        <i className="ri-user-settings-line mr-2"></i>
                        Profil Ayarları
                      </button>
                      
                      <button
                        onClick={handleLogout}
                        className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 cursor-pointer"
                      >
                        <i className="ri-logout-box-line mr-2"></i>
                        Çıkış Yap
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {showUserMenu && (
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setShowUserMenu(false)}
          ></div>
        )}
      </header>

      {/* Main Content */}
      <div className="flex">
        {/* Sidebar */}
        <div className="w-64 bg-white shadow-sm min-h-screen">
          <div className="p-6">
            <nav className="space-y-2">
              <a
                href="#users"
                onClick={(e) => {
                  e.preventDefault();
                  setActiveTab('users');
                }}
                className={`flex items-center px-3 py-2 text-sm font-medium rounded-md cursor-pointer ${
                  activeTab === 'users'
                    ? 'bg-blue-50 text-blue-700'
                    : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                <i className="ri-user-settings-line mr-3"></i>
                Kullanıcı Yönetimi
              </a>
              
              <a
                href="#meetings"
                onClick={(e) => {
                  e.preventDefault();
                  setActiveTab('meetings');
                }}
                className={`flex items-center px-3 py-2 text-sm font-medium rounded-md cursor-pointer ${
                  activeTab === 'meetings'
                    ? 'bg-blue-50 text-blue-700'
                    : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                <i className="ri-calendar-line mr-3"></i>
                Toplantı Yönetimi
              </a>
              
              <a
                href="#reports"
                onClick={(e) => {
                  e.preventDefault();
                  setActiveTab('reports');
                }}
                className={`flex items-center px-3 py-2 text-sm font-medium rounded-md cursor-pointer ${
                  activeTab === 'reports'
                    ? 'bg-blue-50 text-blue-700'
                    : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                <i className="ri-bar-chart-line mr-3"></i>
                Raporlar
              </a>
              
              <a
                href="#settings"
                onClick={(e) => {
                  e.preventDefault();
                  setActiveTab('settings');
                }}
                className={`flex items-center px-3 py-2 text-sm font-medium rounded-md cursor-pointer ${
                  activeTab === 'settings'
                    ? 'bg-blue-50 text-blue-700'
                    : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                <i className="ri-settings-3-line mr-3"></i>
                Ayarlar
              </a>
            </nav>
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1 p-8">
          {activeTab === 'users' && <UserManagement />}
          {activeTab === 'meetings' && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Toplantı Yönetimi</h2>
              <p className="text-gray-600">Toplantı yönetimi özellikleri yakında eklenecek.</p>
            </div>
          )}
          {activeTab === 'reports' && <Reports />}
          {activeTab === 'settings' && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">Sistem Ayarları</h2>
                <p className="text-sm text-gray-600 mt-1">Sistem genelindeki ayarları buradan yönetebilirsiniz.</p>
              </div>

              <div className="p-6 space-y-6">
                {/* İkram Bildirimleri Ayarları */}
                <div className="bg-gray-50 rounded-lg p-6">
                  <div className="flex items-center mb-4">
                    <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center mr-3">
                      <i className="ri-cake-3-line text-orange-600"></i>
                    </div>
                    <div>
                      <h3 className="text-lg font-medium text-gray-900">İkram Bildirimleri</h3>
                      <p className="text-sm text-gray-600">Toplantılarda ikram seçildiğinde bildirim gönderilecek e-posta adresi</p>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Bildirim E-posta Adresi
                      </label>
                      <div className="flex space-x-3">
                        <input
                          type="email"
                          value={cateringEmail}
                          onChange={(e) => setCateringEmail(e.target.value)}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                          placeholder="ornek@sirket.com"
                        />
                        <button
                          onClick={saveCateringSettings}
                          disabled={cateringEmailLoading}
                          className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors flex items-center whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {cateringEmailLoading ? (
                            <>
                              <i className="ri-loader-4-line animate-spin mr-2"></i>
                              Kaydediliyor...
                            </>
                          ) : (
                            <>
                              <i className="ri-save-line mr-2"></i>
                              Kaydet
                            </>
                          )}
                        </button>
                      </div>
                      
                      {cateringEmailStatus === 'success' && (
                        <div className="mt-2 flex items-center text-sm text-green-600">
                          <i className="ri-check-line mr-1"></i>
                          E-posta adresi başarıyla kaydedildi!
                        </div>
                      )}
                      
                      {cateringEmailStatus === 'error' && (
                        <div className="mt-2 flex items-center text-sm text-red-600">
                          <i className="ri-error-warning-line mr-1"></i>
                          E-posta adresi kaydedilemedi. Lütfen tekrar deneyin.
                        </div>
                      )}
                    </div>

                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="flex items-start">
                        <i className="ri-information-line text-blue-600 mr-2 mt-0.5"></i>
                        <div className="text-sm text-blue-800">
                          <p className="font-medium mb-1">Bilgilendirme:</p>
                          <p>Toplantı rezervasyonlarında ikram seçeneği işaretlendiğinde, burada belirtilen e-posta adresine otomatik bildirim gönderilecektir. Bu sayede ikram hazırlıkları zamanında yapılabilir.</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Gelecekte eklenebilecek diğer ayarlar için yer tutucu */}
                <div className="bg-gray-50 rounded-lg p-6">
                  <div className="flex items-center mb-4">
                    <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center mr-3">
                      <i className="ri-settings-4-line text-gray-600"></i>
                    </div>
                    <div>
                      <h3 className="text-lg font-medium text-gray-900">Genel Ayarlar</h3>
                      <p className="text-sm text-gray-600">Diğer sistem ayarları yakında eklenecek</p>
                    </div>
                  </div>
                  
                  <div className="text-center py-8">
                    <i className="ri-tools-line text-4xl text-gray-400 mb-2"></i>
                    <p className="text-gray-500">Ek ayarlar geliştirme aşamasında...</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
